import {Component, OnDestroy, OnInit} from "@angular/core";
import {NavigationEnd, Router} from "@angular/router";
import {Constants} from "../../constants";
import {uxCartRouterCommands} from "../../util/locale-util";
import {INavItem, ITag} from "../../cart/catalog/catalog-models";
import {LoggerService} from "../../common/logger.service";
import {UserService} from "../../common/user.service";
import {SessionService} from "../../common/session.service";
import {TranslateService} from "../../translation/translate.service";
import {BaseComponent} from "../../base.component";
import {ISponsorDetails} from "../../social/social-models";
import * as _ from 'lodash';
import {IProduct} from "../../product/product-models";
import {IShopUser, IWarehouseConfig} from "../../session/customer-models";
import {CartService} from "../../cart/cart.service";
import {CatalogService} from "../../cart/catalog/catalog.service";
import {AlertUtilService} from "../../alert/alert-util.service";
import {AlertService} from "../../alert/alert.service";
import {from, Observable, Subscription} from "rxjs";
import {ConfirmDialogService} from "../../common/confirm-dialog/confirm-dialog.service";
import {Util} from "../../util/util";
import {AutoOrderService} from "../../autoorder/manage/auto-order.service";
import {environment} from "../../../../environments/environment";
import {UsanaCookieService} from "../../common/usana-cookie.service";
import {CategoryService} from "../../category/category.service";
import {filter, take} from "rxjs/operators";
import {RouteUtil} from "../../util/route-util";
import {HeaderService} from "../header.service";
import {LoginModalService} from "../../login/login-modal.service";
import {SsoUtil} from "../../login/sso-util";
import {SponsorService} from "../../common/sponsor.service";
import {LocaleSwitcherService} from "../../locale/locale-switcher.service";
import {CustomerUtil} from "../../util/customer-util";
import {ProductUtil} from "../../util/product-util";
import {AuthUtil} from "../../auth/auth-util";
import {LoginRequestState} from "../../common/general-models";

@Component({
    selector: 'main-header',
    templateUrl: './main-header.component.html',
    styleUrls: ['./main-header.component.scss'],
    standalone: false
})
export class MainHeaderComponent extends BaseComponent implements OnInit, OnDestroy {

    showHeader: boolean = true;
    isDotcomRoute: boolean = false;
    nav$: Observable<INavItem[]>;
    bodyBenefits$: Observable<ITag[]>;
    lifestyles$: Observable<ITag[]>;
    welcomeName: string;
    aoDropdownDisabled: boolean = false;
    displayLoginButton: boolean = false;
    displayLocaleSwitcherButton: boolean = false;
    limitedShopGroup: boolean = false;
    userLanguage: string;
    userCountry: string;
    sponsorDetails: ISponsorDetails;
    sponsorName: string;
    displaySponsorDetails: boolean = false;
    currentWarehouse: string = '';
    logoUrl: string = '';
    mobileSideMenuLogoUrl: string = '';
    mobileMenuCategories: any = {};
    mobileHeaderLogoUrl: string = '';
    isSideMenuOpen: boolean = false;
    homeLinkEnabled: boolean = true;
    sideNavShowIconCategories: boolean = false;
    headerSubscriptions: Subscription;
    showBaoying: boolean = false;
    useBaoying: boolean = false;
    hubLinkUser: boolean = false;
    toggleEventClicked: boolean = false;

    constructor(
        loggerService: LoggerService,
        userService: UserService,
        private cartService: CartService,
        private sponsorService: SponsorService,
        private confirmDialogService: ConfirmDialogService,
        private sessionService: SessionService,
        private alertUtilService: AlertUtilService,
        private alertService: AlertService,
        private translateService: TranslateService,
        private catalogService: CatalogService,
        private autoOrderService: AutoOrderService,
        private usanaCookieService: UsanaCookieService,
        private loginModalService: LoginModalService,
        private headerService: HeaderService,
        private categoryService: CategoryService,
        private router: Router,
        private localeSwitcherService: LocaleSwitcherService
    ) {
        super(userService, loggerService);
    }

    ngOnInit(): void {
        this.getNavItems();
        this.nav$ = this.categoryService.getNavCategories();
        this.bodyBenefits$ = this.categoryService.getBodyBenefits();
        this.lifestyles$ = this.categoryService.getLifeStyles();

        // This object is used for the mobile menu categories and has to contain only boolean props.
        this.mobileMenuCategories = {
            isLifestyleOpen: false,
            isCategoriesOpen: false,
            isBodyBenefitsOpen: false
        };

        this.isDotcomRoute = RouteUtil.isDotcomRoute(this.router.url);
        this.headerSubscriptions = this.autoOrderService.autoOrderDropdownDisabled.subscribe((value: boolean) => {
            this.aoDropdownDisabled = value;
        });
        this.headerSubscriptions.add(this.router.events.pipe(
            filter(e => e instanceof NavigationEnd)
        ).subscribe(() => {
            this.isDotcomRoute = RouteUtil.isDotcomRoute(this.router.url);
        }));

        this.headerSubscriptions.add(this.sponsorService.sponsorVisible$().subscribe((sponsorDetails: ISponsorDetails | null) => {
            this.setSponsorDetails(sponsorDetails);
        }));

        this.headerSubscriptions.add(from(this.getCustomerData()).subscribe((user: IShopUser) => {
            this.initComponent(user);
        }));

        this.headerSubscriptions.add(this.headerService.showHeaderSubject.subscribe((value: boolean) => {
            this.showHeader = value;
        }));
    }

    ngOnDestroy(): void {
        this.headerSubscriptions.unsubscribe();
    }

    get showClassicShopLink(): boolean {
        return !!environment.CLASSIC_SHOPPING_ENABLED && (!!this.authenticated && !this.nfrShopping && !this.employee);
    }

    get showGoToHub(): boolean {
        return this.authenticated &&
            !(this.limitedShopping || this.affiliate || this.preferredCustomer) &&
            (this.associate || this.employee || this.chinaCustomer || this.useBaoying);
    }

    // On /ux/dotcom, hub-eligible users see the legacy "the hub" link in place of the account
    // dropdown. Everyone else (logged out, or logged-in non-hub types) keeps the cart behavior.
    get showDotcomHubLink(): boolean {
        return this.isDotcomRoute && this.authenticated && this.hubLinkUser;
    }

    searchItemSelected(selectedItem: any) {
        const product: IProduct = selectedItem.item.product;
        this.log.debug("searchItemSelected product", product);
        if (selectedItem.shiftKeyEnter && _.includes(product.shopModes, Constants.SHOP)) {
            this.log.debug("searchItemSelected", " shift + enter pressed, quick add!");
            this.cartService.addToCart(product).subscribe((success: boolean) => {
                if (success) {
                    this.alertUtilService.alertAddedToCart(product);
                }
            });
        } else {
            this.log.debug("searchItemSelected", " shift + enter NOT pressed, go to detail page");
            this.router.navigate(uxCartRouterCommands(Constants.PRODUCT_STATE, ProductUtil.convertUsanaIdToDash(product.usanaId)));
        }
    }

    getNavItems(): void {
        this.categoryService.loadNavCategories().subscribe();
        this.categoryService.loadNavTags().subscribe();
    }

    private initComponent(user: IShopUser): void {
        this.log.debug("Updating nav header properties");
        this.sessionService.getCurrentWarehouse().then((currentWarehouse: IWarehouseConfig) => {
            this.currentWarehouse = currentWarehouse.warehouse;
        });
        this.homeLinkEnabled = !this.nfrMhpShoppingOnly;
        this.welcomeName = CustomerUtil.getDisplayFirstName(this.authenticated, user);
        this.displayLoginButton = !this.authenticated && !this.limitedShopping;
        this.displayLocaleSwitcherButton = user.config.showLocaleSwitcher;

        this.limitedShopGroup = this.nfrMhpShoppingOnly || this.limitedShopping;
        this.userCountry = user.currentCountry;
        this.translateService.translate("cart.locale.switcher.language.abbreviation." + user.customer.language.toLowerCase()).then((data: any) => {
            this.userLanguage = data;
        });
        if (user.config.showBaoying) {
            this.showBaoying = true;
            this.logoUrl = Constants.URL_FOR_BAOYING_LOGO;
            this.mobileSideMenuLogoUrl = Constants.URL_FOR_BAOYING_LOGO;
            this.mobileHeaderLogoUrl = Constants.URL_FOR_BAOYING_LOGO;
        } else {
            this.logoUrl = Constants.URL_FOR_USANA_LOGO;
            this.mobileSideMenuLogoUrl = Constants.URL_FOR_USANA_MOBILE_SIDE_MENU_LOGO;
            this.mobileHeaderLogoUrl = Constants.URL_FOR_USANA_LOGO;
        }
        this.useBaoying = user.country === Constants.COUNTRY_CN; // do not use currentCountry
        this.hubLinkUser = AuthUtil.isHubLinkUser(user);
    }

    // Visible-or-null comes from SponsorService.sponsorVisible$(), which already filters out
    // authenticated users, PC referrals, and missing sponsors. So we just render or clear.
    setSponsorDetails(sponsorDetails: ISponsorDetails | null): void {
        if (sponsorDetails) {
            this.sponsorDetails = sponsorDetails;
            this.sponsorName = sponsorDetails.sponsorOtherName || sponsorDetails.sponsorName;
            this.displaySponsorDetails = !!this.sponsorName;
        } else {
            this.sponsorDetails = undefined;
            this.sponsorName = undefined;
            this.displaySponsorDetails = false;
        }
    }

    private resetMobileMenuCategories(): void {
        _.keys(this.mobileMenuCategories).forEach(key => {
            this.mobileMenuCategories[key] = false;
        });
    }

    login(): void {
        // On /ux/dotcom, legacy dotcom landed the user on the Hub after login rather than
        // returning them to the dotcom page. Preserve that by overriding the post-login redirect.
        const loginRequestState = new LoginRequestState();
        if (this.isDotcomRoute) {
            loginRequestState.redirectUrl = this.getHubLink();
        }
        this.loginModalService.openLogin(loginRequestState).pipe(take(1)).subscribe();
    }

    getHubLink() {
        return Util.getBaseHubUrl(this.useBaoying);
    }

    getPersonalInfoLink() {
        return SsoUtil.getProfileManagerUrl();
    }

    getTaxInfoLink() {
        return `${Util.getInternalBaseUrl()}${Constants.URL_HUB}${Constants.URL_TAX_INFORMATION}`;
    }

    getNotificationLink() {
        return `${Util.getInternalBaseUrl()}${Constants.URL_HUB}${Constants.URL_NOTIFICATION}`;
    }

    getAffiliateOrderHistoryLink() {
        return `${Util.getInternalBaseUrl()}${Constants.URL_HUB}${Constants.URL_AFFILIATE_ORDER_HISTORY}`;
    }

    getReturnsLink() {
        return `${Util.getInternalSsoUrl()}${Constants.URL_RETURNS}`;
    }

    getNfrLink() {
        return `${Util.getInternalBaseUrl()}${Constants.URL_HUB}${Constants.URL_NFR}`;
    }

    mobileNavigateTo(url: string) {
        this.isSideMenuOpen = false;
        Util.redirect(url, true);
    }

    closeAllAlerts() {
        this.alertService.closeAllAlerts();
    }

    openLocaleSwitcher() {
        this.localeSwitcherService.openLocaleSwitcher();
    }

    setMobileNav() {
        this.resetMobileMenuCategories();
        this.isSideMenuOpen = true;
    }

    clearCategoryFilters() {
        this.catalogService.clearFilters();
    }

    goToContactCustomer() {
        if (this.isWechatBrowser()) {
            window.open(Constants.URL_WECHAT_SUPPORT, '_blank');
        }else {
            this.confirmDialogService.open("md", {title: "cart.contact.customer.service", showCloseIcon: true}, [{
                body: "cart.contact.customer.phone.number",
                translate: true
            }], {}, {});
        }
    }

    openExitNfrDialog() {
        const confirmExit = () => {
            this.removeShopMode(Constants.NFR_SHOPPING);
        };

        this.confirmDialogService.open("md", {title: "cart.exit.nfr.confirm.title"}, [{
            body: "cart.exit.nfr.confirm.body.text",
            translate: true
        }], {confirmAction: confirmExit}, {show: true});
    }

    toggleEmployeePriceType() {
        this.cartService.clearCart().subscribe(() => {
            if (this.payingWithUsanaBucks) {
                this.usanaCookieService.setCookie(Constants.EMPLOYEE_PERSONAL_SHOPPING, true, null);
            } else {
                this.usanaCookieService.removeCookie(Constants.EMPLOYEE_PERSONAL_SHOPPING);
            }
            this.router.navigate(uxCartRouterCommands(Constants.RELOAD_STATE));
        });
    }

    toggleEventShopping() {
        if (this.eventShoppingEligible && !this.toggleEventClicked) {
            this.toggleEventClicked = true;
            if (this.eventShopping) {
                this.removeShopMode(Constants.EVENT_SHOPPING);
            } else {
                this.usanaCookieService.removeCookie(Constants.STANDARD_SHOPPING);
                this.usanaCookieService.setCookie(Constants.EVENT_SHOPPING, true, null);
                this.router.navigate(uxCartRouterCommands(Constants.RELOAD_STATE));
            }
        }
    }

    removeShopMode(cookieName: string) {
        // set standard shopping and reload shop user
        this.usanaCookieService.removeCookie(cookieName);
        this.usanaCookieService.setCookie(Constants.STANDARD_SHOPPING, true, null);

        if (this.useBaoying) { // even if the customer isn't on the baoying domain, we still want to redirect to baoying
            Util.redirect(this.getHubLink(), false);
        } else {
            /* this can be reworked after the shop user has been moved to the gateway cache.
                    * currently we need to force a shop user reload before reloading the page
                    * to guarantee removing the shop mode and prevent a race condition
                    * of returning the previous user/cart to the front end after reload */
            this.sessionService.populateShopUser().subscribe({
                next: () => this.router.navigate(uxCartRouterCommands(Constants.RELOAD_STATE)),
                error: () => this.router.navigate(uxCartRouterCommands(Constants.RELOAD_STATE))
            });
        }
    }
}
